// src/pages/ExternalService/ExternalServiceBikeFlow.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy } from "react-table";
import axios from "axios";
import { format } from "date-fns";

/* ───────────────── helper UI bits ───────────────── */
const ActionBtn = ({ label, colour, onClick }) => (
  <button
    onClick={onClick}
    className={`${colour} text-white text-xs px-3 py-1 rounded hover:opacity-90`}
  >
    {label}
  </button>
);

/* full‑height card‑table wrapper (identical layout as MechanicTodo) */
const CardTable = ({ title, rows, columns, badgeColour = "bg-joule" }) => {
  const tableInstance = useTable(
    {
      columns,
      data: rows,
      initialState: { sortBy: [{ id: "date", desc: true }] },
    },
    useSortBy
  );

  return (
    <div className="w-full lg:w-1/2 p-4">
      <div className="h-[calc(100vh-2rem)] flex flex-col rounded-xl border border-gray-300 shadow-xl bg-white">
        <header className="px-5 py-4 border-b border-gray-200 flex justify-center items-center">
          <h2 className="text-lg font-semibold flex items-center">
            {title}
            <span
              className={`${badgeColour} text-white text-xs px-3 py-1 rounded-full ml-3`}
            >
              {tableInstance.rows.length}
            </span>
          </h2>
        </header>

        <div className="flex-1 overflow-auto">
          <table
            {...tableInstance.getTableProps()}
            className="min-w-full divide-y divide-gray-200"
          >
            <thead className="bg-gray-800 text-white sticky top-0 z-10">
              {tableInstance.headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(
                        col.getSortByToggleProps?.() || {}
                      )}
                      className="px-4 py-4 text-xs font-semibold uppercase tracking-wider text-center"
                    >
                      {col.render("Header")}
                      {col.isSorted && (col.isSortedDesc ? " 🔽" : " 🔼")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody
              {...tableInstance.getTableBodyProps()}
              className="divide-y divide-gray-100"
            >
              {tableInstance.rows.map((row) => {
                tableInstance.prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-50">
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="px-4 py-3 text-sm text-center whitespace-nowrap"
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ───────────────── main page ───────────────── */
const ExternalServiceBikeFlow = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  /* fetch once */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/external-bookings");
        setBookings(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* optimistic update helper */
  const updateLocal = useCallback(
    (updated) =>
      setBookings((prev) =>
        prev.map((b) => (b._id === updated._id ? updated : b))
      ),
    []
  );

  /* API helpers (memoised) */
  const markReceived = useCallback(
    async (id) => {
      try {
        const { data } = await axios.patch(
          `/api/external-bookings/${id}/status`,
          { status: "Received" }
        );
        updateLocal(data);
      } catch (e) {
        console.error(e);
      }
    },
    [updateLocal]
  );

  const markReturned = useCallback(
    async (id) => {
      try {
        const { data } = await axios.patch(
          `/api/external-bookings/${id}/status`,
          { status: "Closed" }
        );
        updateLocal(data);
      } catch (e) {
        console.error(e);
      }
    },
    [updateLocal]
  );

  /* status groups */
  const incoming = useMemo(
    () => bookings.filter((b) => b.status === "New"),
    [bookings]
  );
  const outgoing = useMemo(
    () => bookings.filter((b) => b.status === "Completed"),
    [bookings]
  );

  /* shared base columns (memoised once) */
  const baseCols = useMemo(
    () => [
      {
        Header: "Bike",
        accessor: (d) => `${d.bikeBrand} ${d.bikeModel}`,
        id: "bike",
      },
      {
        Header: "Package",
        accessor: "packageName",
      },
      {
        Header: "Customer",
        accessor: (d) => `${d.firstName} ${d.lastName}`,
        id: "cust",
      },
    ],
    []
  );

  /* incoming table columns */
  const incomingCols = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "bookingDate",
        id: "date",
        Cell: ({ value }) => (value ? format(new Date(value), "dd.MM") : "—"),
      },
      { Header: "Booking ID", accessor: "externalBookingId" },
      ...baseCols,
      {
        Header: "",
        accessor: "_id",
        Cell: ({ value }) => (
          <ActionBtn
            label="Mark received"
            colour="bg-green-600"
            onClick={() => markReceived(value)}
          />
        ),
      },
    ],
    [baseCols, markReceived]
  );

  /* outgoing table columns */
  const outgoingCols = useMemo(
    () => [
      {
        Header: "Date",
        accessor: (row) => row.returnDate || row.completedAt,
        id: "date",
        Cell: ({ value }) => (value ? format(new Date(value), "dd.MM") : "—"),
      },
      { Header: "Booking ID", accessor: "externalBookingId" },
      ...baseCols,
      {
        Header: "",
        accessor: "_id",
        Cell: ({ value }) => (
          <ActionBtn
            label="Mark returned"
            colour="bg-joule"
            onClick={() => markReturned(value)}
          />
        ),
      },
    ],
    [baseCols, markReturned]
  );

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="flex flex-wrap">
      <CardTable
        title="🚚 Incoming bikes"
        rows={incoming}
        columns={incomingCols}
        badgeColour="bg-yellow-600"
      />

      <CardTable
        title="🏠 Waiting for return"
        rows={outgoing}
        columns={outgoingCols}
        badgeColour="bg-blue-600"
      />
    </div>
  );
};

export default ExternalServiceBikeFlow;
